/**
 * SEO Structured Data Component
 * 
 * Adds Schema.org JSON-LD structured data to pages for better search engine visibility.
 * Supports: Organization, SportsActivityLocation, Course, Event schemas.
 *
 * Tenant-aware (iter317): every helper accepts `businessName` (and where
 * relevant, `businessSettings`) so SEO never leaks DAS's "Delhi Capitals
 * Cricket Academy NJ" to other white-label tenants. Callers should pass
 * the active tenant's display name (e.g. `branding?.business_name` or
 * `contactInfo?.business_name`); the neutral "Cricket Academy" fallback
 * only kicks in for an unresolved-tenant render.
 */

import { useEffect } from 'react';

const DEFAULT_BUSINESS_NAME = 'Cricket Academy';

/**
 * Base organization schema for the active tenant.
 */
export const getOrganizationSchema = (businessSettings = {}, businessName = DEFAULT_BUSINESS_NAME) => ({
  "@context": "https://schema.org",
  "@type": "SportsActivityLocation",
  "@id": window.location.origin,
  "name": businessSettings.academy_name || businessName || DEFAULT_BUSINESS_NAME,
  "description": businessSettings.description || "Premier cricket coaching academy offering professional training programs for all ages and skill levels.",
  "url": window.location.origin,
  "telephone": businessSettings.phone || "",
  "email": businessSettings.email || "",
  "address": businessSettings.address ? {
    "@type": "PostalAddress",
    "streetAddress": businessSettings.address.street || "",
    "addressLocality": businessSettings.address.city || "",
    "addressRegion": businessSettings.address.state || "",
    "postalCode": businessSettings.address.zip || "",
    "addressCountry": "US"
  } : undefined,
  "sameAs": [
    businessSettings.facebook_url,
    businessSettings.instagram_url,
    businessSettings.twitter_url
  ].filter(Boolean),
  "sport": "Cricket",
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Indoor Training Lanes" },
    { "@type": "LocationFeatureSpecification", "name": "Bowling Machines" },
    { "@type": "LocationFeatureSpecification", "name": "Video Analysis" },
    { "@type": "LocationFeatureSpecification", "name": "Professional Coaching" }
  ],
  "priceRange": "$$"
});

/**
 * Course schema for training programs
 */
export const getCourseSchema = (program, businessName = DEFAULT_BUSINESS_NAME) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": program.name,
  "description": program.description || `Professional cricket training program: ${program.name}`,
  "provider": {
    "@type": "Organization",
    "name": businessName || DEFAULT_BUSINESS_NAME,
    "sameAs": window.location.origin
  },
  "courseCode": program.id,
  "educationalLevel": program.skill_level || "All Levels",
  "teaches": "Cricket skills including batting, bowling, fielding, and match strategy",
  "availableLanguage": "en",
  "offers": {
    "@type": "Offer",
    "price": program.price || program.monthly_price || 0,
    "priceCurrency": "USD",
    "availability": program.is_active !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    "validFrom": new Date().toISOString().split('T')[0]
  },
  "hasCourseInstance": program.classes?.map(cls => ({
    "@type": "CourseInstance",
    "name": cls.name,
    "courseSchedule": {
      "@type": "Schedule",
      "byDay": cls.schedule_day,
      "startTime": cls.start_time,
      "endTime": cls.end_time
    },
    "instructor": cls.coach_name ? {
      "@type": "Person",
      "name": cls.coach_name
    } : undefined
  })) || []
});

/**
 * Event schema for tournaments
 */
export const getTournamentSchema = (tournament, businessName = DEFAULT_BUSINESS_NAME) => ({
  "@context": "https://schema.org",
  "@type": "SportsEvent",
  "name": tournament.name,
  "description": tournament.details || `Cricket tournament: ${tournament.name}`,
  "sport": "Cricket",
  "startDate": tournament.start_date,
  "endDate": tournament.end_date,
  "location": tournament.location_type === "travel" ? {
    "@type": "Place",
    "name": tournament.location_name || "Tournament Venue"
  } : {
    "@type": "SportsActivityLocation",
    "name": businessName || DEFAULT_BUSINESS_NAME
  },
  "organizer": {
    "@type": "Organization",
    "name": businessName || DEFAULT_BUSINESS_NAME,
    "url": window.location.origin
  },
  "eventStatus": tournament.status === "cancelled" 
    ? "https://schema.org/EventCancelled" 
    : "https://schema.org/EventScheduled",
  "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
  "offers": tournament.initial_payment_amount ? {
    "@type": "Offer",
    "price": tournament.initial_payment_amount,
    "priceCurrency": "USD",
    "availability": tournament.status === "registration_open" 
      ? "https://schema.org/InStock" 
      : "https://schema.org/SoldOut"
  } : undefined
});

/**
 * FAQ schema for common questions
 */
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Local Business schema for landing page
 */
export const getLocalBusinessSchema = (businessName = DEFAULT_BUSINESS_NAME, businessSettings = {}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": businessName || DEFAULT_BUSINESS_NAME,
  "image": `${window.location.origin}/logo.png`,
  "description": businessSettings.description || "Professional cricket coaching academy.",
  "@id": window.location.origin,
  "url": window.location.origin,
  "priceRange": "$$",
  "address": businessSettings.address ? {
    "@type": "PostalAddress",
    "streetAddress": businessSettings.address.street || "",
    "addressLocality": businessSettings.address.city || "",
    "addressRegion": businessSettings.address.state || "",
    "postalCode": businessSettings.address.zip || "",
    "addressCountry": "US"
  } : undefined,
});

/**
 * Breadcrumb schema
 */
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

/**
 * Hook to inject JSON-LD structured data into the page
 */
export const useStructuredData = (schemas) => {
  useEffect(() => {
    // Remove any existing schema scripts we've added
    const existingScripts = document.querySelectorAll('script[data-schema="true"]');
    existingScripts.forEach(script => script.remove());

    // Add new schema scripts
    const schemaArray = Array.isArray(schemas) ? schemas : [schemas];
    
    schemaArray.forEach((schema, index) => {
      if (!schema) return;
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'true');
      script.setAttribute('data-schema-index', index.toString());
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Cleanup on unmount
    return () => {
      const scripts = document.querySelectorAll('script[data-schema="true"]');
      scripts.forEach(script => script.remove());
    };
  }, [schemas]);
};

/**
 * Component to render structured data
 * Usage: <StructuredData schema={getOrganizationSchema()} />
 */
const StructuredData = ({ schema }) => {
  useStructuredData(schema);
  return null;
};

export default StructuredData;
